import { useTraderTime } from '../../contexts/TraderTimeContext';
import React, { useEffect, useState } from 'react';
import { adminAPI, traderDevicesAPI, AdminDeviceEventFilters, AdminDeviceEventOptions, AdminDeviceEventPage } from '../../lib/api';
import { bankDisplayName } from '../../lib/kgsBanks';
import { formatTraderDate, traderInputToUTC, timezoneLabel } from '../../lib/traderTime';
import { Button } from '../ui/button';

const emptyFilters = { trader_id: '', device_id: '', date_from: '', date_to: '', transaction_id: '',
    automation_status: '' as NonNullable<AdminDeviceEventFilters['automation_status']> | '' };
const control = 'rounded border p-2 bg-white dark:bg-slate-800 w-full';

const automationLabels = { triggered: 'Сработала', pending: 'В обработке', retrying: 'Ошибка обработки · ожидается повтор', not_triggered: 'Не сработала' };

export const DeviceEventsJournal: React.FC<{ audience: 'admin' | 'trader' }> = ({ audience }) => {
    const { offsetMinutes } = useTraderTime();
    const isAdmin = audience === 'admin';
    const displayOffset = isAdmin ? 0 : offsetMinutes;
    const dateLabel = (value: string | number | null) => formatTraderDate(value, displayOffset);
    const [draft, setDraft] = useState(emptyFilters);
    const [query, setQuery] = useState<AdminDeviceEventFilters & { page: number; size: number }>({ page: 1, size: 20 });
    const [data, setData] = useState<AdminDeviceEventPage | null>(null);
    const [options, setOptions] = useState<AdminDeviceEventOptions>({ traders: [], devices: [] });
    const [loading, setLoading] = useState(true);
    const [optionsLoading, setOptionsLoading] = useState(true);
    const [error, setError] = useState('');
    const [optionsError, setOptionsError] = useState('');
    const [validation, setValidation] = useState('');
    const [refresh, setRefresh] = useState(0);

    useEffect(() => { setQuery(current => current.page === 1 ? current : { ...current, page: 1 }); }, [displayOffset]);

    useEffect(() => {
        let active = true;
        setLoading(true); setError('');
        const utcQuery = { ...query,
            date_from: query.date_from ? traderInputToUTC(query.date_from, displayOffset).toISOString() : undefined,
            date_to: query.date_to ? traderInputToUTC(query.date_to, displayOffset).toISOString() : undefined,
        };
        const { trader_id, ...traderQuery } = utcQuery;
        const request = isAdmin ? adminAPI.getDeviceEvents(utcQuery) : traderDevicesAPI.events(traderQuery);
        request.then(result => {
            if (!active) return;
            if (query.page > Math.max(1, result.pages)) {
                setQuery(current => ({ ...current, page: Math.max(1, result.pages) }));
            } else setData(result);
        }).catch(() => { if (active) setError('Не удалось загрузить пуши'); })
            .finally(() => { if (active) setLoading(false); });
        return () => { active = false; };
    }, [query, refresh, isAdmin, displayOffset]);

    useEffect(() => {
        let active = true;
        setOptionsLoading(true); setOptionsError('');
        const request = isAdmin ? adminAPI.getDeviceEventOptions(draft.trader_id || undefined)
            : traderDevicesAPI.eventOptions().then(result => ({ traders: [], devices: result.devices }));
        request.then(result => {
            if (active) setOptions(result);
        }).catch(() => { if (active) setOptionsError('Не удалось загрузить варианты фильтров'); })
            .finally(() => { if (active) setOptionsLoading(false); });
        return () => { active = false; };
    }, [draft.trader_id, refresh, isAdmin]);

    const apply = (event: React.FormEvent) => {
        event.preventDefault();
        const from = draft.date_from ? traderInputToUTC(draft.date_from, displayOffset) : null;
        const to = draft.date_to ? traderInputToUTC(draft.date_to, displayOffset) : null;
        if ((from && !Number.isFinite(from.getTime())) || (to && !Number.isFinite(to.getTime())) || (from && to && from > to)) {
            setValidation('Укажите корректный период: начало должно быть не позже конца'); return;
        }
        setValidation('');
        setQuery({ page: 1, size: query.size, trader_id: draft.trader_id || undefined,
            transaction_id: draft.transaction_id.trim() || undefined,
            automation_status: draft.automation_status || undefined,
            device_id: draft.device_id || undefined, date_from: draft.date_from || undefined, date_to: draft.date_to || undefined });
    };

    return <div className="space-y-4">
        <div className="flex flex-wrap justify-between gap-3">
            <div><h2 className="text-xl font-semibold">Пуши</h2>
                <p className="text-sm text-gray-500 dark:text-slate-400">Все уведомления разрешённых банковских приложений, независимо от подтверждения сделки.</p>
                {data && <p className="text-sm text-gray-500 dark:text-slate-400">Срок хранения: {data.retention_days} дн.</p>}
            </div>
            <Button variant="outline" onClick={() => setRefresh(value => value + 1)}>Обновить</Button>
        </div>
        <p className="text-sm">Все даты и фильтр периода — в {timezoneLabel(displayOffset)}.</p>
        <form onSubmit={apply} className="flex flex-wrap items-end gap-3">
            <label className="text-sm">Статус автоматики<select aria-label="Статус автоматики" className={control} value={draft.automation_status}
                onChange={event => setDraft(current => ({ ...current, automation_status: event.target.value as typeof draft.automation_status }))}>
                <option value="">Все статусы</option>
                {(['triggered', 'not_triggered', 'pending', 'retrying'] as const).map(status =>
                    <option key={status} value={status}>{automationLabels[status]}</option>)}
            </select></label>
            <label className="text-sm">ID транзакции<input aria-label="ID транзакции" className={control}
                type="text" placeholder="Полный ID транзакции" maxLength={128} value={draft.transaction_id}
                onChange={event => setDraft(current => ({ ...current, transaction_id: event.target.value }))} /></label>
            {isAdmin && <label className="text-sm">Трейдер<select aria-label="Трейдер" className={control} value={draft.trader_id}
                onChange={event => setDraft(current => ({ ...current, trader_id: event.target.value, device_id: '' }))}>
                <option value="">Все трейдеры</option>
                {options.traders.map(item => <option key={item.id} value={item.id}>{item.name} · {item.id}</option>)}
            </select></label>}
            <label className="text-sm">Устройство<select aria-label="Устройство" className={control} value={draft.device_id} disabled={optionsLoading}
                onChange={event => setDraft(current => ({ ...current, device_id: event.target.value }))}>
                <option value="">Все устройства</option>
                {options.devices.filter(item => !draft.trader_id || item.trader_id === draft.trader_id).map(item =>
                    <option key={item.id} value={item.id}>{item.name} · {item.id}</option>)}
            </select></label>
            <label className="text-sm">С<input aria-label="С" className={control} type="datetime-local" step="1" value={draft.date_from}
                onChange={event => setDraft(current => ({ ...current, date_from: event.target.value }))} /></label>
            <label className="text-sm">До<input aria-label="До" className={control} type="datetime-local" step="1" value={draft.date_to}
                onChange={event => setDraft(current => ({ ...current, date_to: event.target.value }))} /></label>
            <Button type="submit">Применить</Button>
            <Button type="button" variant="outline" onClick={() => {
                setDraft(emptyFilters); setValidation(''); setQuery({ page: 1, size: query.size });
            }}>Сбросить</Button>
        </form>
        {validation && <p role="alert" className="text-red-600">{validation}</p>}
        {optionsError && <div role="alert">{optionsError} <Button variant="outline" onClick={() => setRefresh(value => value + 1)}>Повторить загрузку фильтров</Button></div>}
        {error ? <div role="alert">{error} <Button variant="outline" onClick={() => setRefresh(value => value + 1)}>Повторить</Button></div> :
            loading ? <p role="status">Загрузка пушей…</p> : !data?.items.length ? <p>Пуши не найдены</p> :
                <div className="overflow-x-auto rounded border"><table className="w-full text-sm text-left">
                    <thead><tr>{['Время пуша / ID события', ...(isAdmin ? ['Трейдер'] : []), 'Устройство', isAdmin ? 'Банк / приложение' : 'Банк', 'Сообщение', 'Автоматика / транзакция', ...(isAdmin ? ['Причина', 'Метод'] : []), 'Получено сервером'].map(label =>
                        <th key={label} scope="col" className="p-3">{label}</th>)}</tr></thead>
                    <tbody>{data.items.map(item => <tr key={item.id} className="border-t align-top">
                        <td className="p-3"><div>{dateLabel(item.occurred_at)}</div><div className="text-xs break-all mt-1">{item.event_id}</div></td>
                        {isAdmin && <td className="p-3 break-all">{item.trader_name}{item.trader_name !== item.trader_id && <div className="text-xs">{item.trader_id}</div>}</td>}
                        <td className="p-3 break-all">{item.device_name}{item.device_name !== item.device_id && <div className="text-xs">{item.device_id}</div>}</td>
                        <td className="p-3"><div>{bankDisplayName(item.bank)}</div>{isAdmin && <div className="text-xs break-all">{item.package_name}</div>}</td>
                        <td className="p-3 min-w-[280px] whitespace-pre-wrap break-words"><div className="font-medium">{item.title}</div><div>{item.text || '—'}</div></td>
                        <td className="p-3"><div className={item.automation_status === 'triggered' ? 'text-green-700 dark:text-green-300' : ''}>{automationLabels[item.automation_status]}</div>
                            {item.transaction_id && <><div className="font-mono text-xs break-all select-all mt-1" title="ID транзакции">{item.transaction_id}</div>
                                <div className="text-xs">{item.transaction_status === 'completed' ? 'Завершена' : item.transaction_status === 'confirmed' ? 'Подтверждена · ожидает завершения' : item.transaction_status}</div></>}
                        </td>
                        {isAdmin && <td className="p-3 min-w-[220px]">{item.automation_reason || (item.automation_status === 'triggered' ? '—' : 'Причина не сохранена')}</td>}
                        {isAdmin && <td className="p-3"><div className="font-mono">{item.requisite_type || 'Не определён'}</div>
                            {item.method_source === 'notification' && <div className="text-xs text-gray-500">По формату пуша</div>}
                        </td>}
                        <td className="p-3">{dateLabel(item.server_received_at)}</td>
                    </tr>)}</tbody>
                </table></div>}
        <div className="flex flex-wrap justify-between items-center gap-3 text-sm">
            <label>Записей на странице: <select aria-label="Записей на странице" className="rounded border p-2 bg-white dark:bg-slate-800" value={query.size}
                onChange={event => setQuery(current => ({ ...current, size: Number(event.target.value), page: 1 }))}>
                {[20, 50, 100].map(size => <option key={size} value={size}>{size}</option>)}
            </select></label>
            <div className="flex items-center gap-3">
                {!loading && !error && data && <span>Всего: {data.total} · Страница {data.page} из {Math.max(1, data.pages)}</span>}
                <Button variant="outline" disabled={loading || !!error || query.page === 1} onClick={() => setQuery(current => ({ ...current, page: current.page - 1 }))}>Назад</Button>
                <Button variant="outline" disabled={loading || !!error || !data || query.page >= data.pages} onClick={() => setQuery(current => ({ ...current, page: current.page + 1 }))}>Далее</Button>
            </div>
        </div>
    </div>;
};
